import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { ChevronRight, PenTool, RotateCcw, Check, Loader2, Monitor, Lock, AlertCircle, X, Delete } from "lucide-react";
import axios from "axios";

const API = "/api";

const NumPad = ({ value, onChange, maxLength = 6 }) => {
  const handleKey = (k) => {
    if (k === "del") {
      onChange(value.slice(0, -1));
    } else if (value.length < maxLength) {
      onChange(value + k);
    }
  };
  const keys = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "", "0", "del"];
  return (
    <div className="grid grid-cols-3 gap-3 max-w-[280px] mx-auto">
      {keys.map((k, i) =>
        k === "" ? <div key={i} /> : (
          <button
            key={i}
            type="button"
            onClick={() => handleKey(k)}
            className="h-14 rounded-xl text-xl font-semibold bg-slate-100 hover:bg-slate-200 active:bg-slate-300 text-slate-900 transition-colors flex items-center justify-center"
          >
            {k === "del" ? <Delete className="w-5 h-5" /> : k}
          </button>
        )
      )}
    </div>
  );
};

export default function FirmaKioskPage() {
  const [authenticated, setAuthenticated] = useState(false);
  const [pinInput, setPinInput] = useState("");
  const [pinError, setPinError] = useState("");
  const [checkingPin, setCheckingPin] = useState(true);
  const [status, setStatus] = useState("idle");
  const [step, setStep] = useState(1);
  const [data, setData] = useState(null);
  const [privacyPages, setPrivacyPages] = useState([]);
  const [signatures, setSignatures] = useState([null, null, null]);
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);

  useEffect(() => {
    const checkPin = async () => {
      try {
        const res = await axios.get(`${API}/signatures/kiosk/config`);
        if (!res.data?.pin_required) {
          const tk = await axios.post(`${API}/signatures/kiosk/verify-pin`, { pin: "" });
          sessionStorage.setItem("kiosk_token", tk.data.kiosk_token || "");
          setAuthenticated(true);
        } else if (sessionStorage.getItem("kiosk_token")) {
          setAuthenticated(true);
        }
      } catch {
        if (sessionStorage.getItem("kiosk_token")) setAuthenticated(true);
      }
      setCheckingPin(false);
    };
    checkPin();
  }, []);

  const verifyPin = async () => {
    setPinError("");
    try {
      const res = await axios.post(`${API}/signatures/kiosk/verify-pin`, { pin: pinInput });
      if (res.data.valid) {
        sessionStorage.setItem("kiosk_token", res.data.kiosk_token || "");
        sessionStorage.setItem("kiosk_auth", "true");
        setAuthenticated(true);
      }
    } catch {
      setPinError("PIN non valido");
      setPinInput("");
    }
  };

  const sigIndex = step === 2 ? 0 : step === 4 ? 1 : step === 6 ? 2 : -1;

  useEffect(() => {
    if (status !== "idle") return;
    const interval = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/signatures/kiosk/poll?target=tablet`);
        if (res.data.status === "pending") {
          setData(res.data.data);
          setSignatures([null, null, null]);
          setStep(1);
          setStatus("loading");
          try {
            const pRes = await axios.get(`${API}/signatures/privacy-preview`);
            setPrivacyPages(pRes.data.pages || []);
          } catch {
            setPrivacyPages([]);
          }
          setStatus("active");
        }
      } catch {}
    }, 2000);
    return () => clearInterval(interval);
  }, [status]);

  useEffect(() => {
    if ((step === 2 || step === 4 || step === 6) && canvasRef.current) {
      const canvas = canvasRef.current;
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      const ctx = canvas.getContext("2d");
      ctx.scale(dpr, dpr);
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.strokeStyle = "#000";
      ctx.lineWidth = 1.2;
      setHasDrawn(false);
    }
  }, [step]);

  const lastPosRef = useRef(null);
  const getPos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top, pressure: e.pressure || 0.5 };
  };

  const startDraw = (e) => {
    e.preventDefault();
    setIsDrawing(true);
    const pos = getPos(e);
    lastPosRef.current = pos;
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const pos = getPos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineWidth = Math.max(0.8, 1.2 * (pos.pressure * 2));
    const prev = lastPosRef.current;
    if (prev) {
      const mx = (prev.x + pos.x) / 2;
      const my = (prev.y + pos.y) / 2;
      ctx.quadraticCurveTo(prev.x, prev.y, mx, my);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(mx, my);
    }
    lastPosRef.current = pos;
    setHasDrawn(true);
  };

  const endDraw = (e) => {
    if (e) e.preventDefault();
    setIsDrawing(false);
    lastPosRef.current = null;
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const dpr = window.devicePixelRatio || 1;
    ctx.clearRect(0, 0, canvas.width / dpr, canvas.height / dpr);
    setHasDrawn(false);
  };

  const captureCurrentCanvas = () => {
    const canvas = canvasRef.current;
    return canvas.toDataURL("image/png").split(",")[1];
  };

  const saveCurrentSignature = () => {
    const b64 = captureCurrentCanvas();
    const newSigs = [...signatures];
    newSigs[sigIndex] = b64;
    setSignatures(newSigs);
    setStep(step + 1);
  };

  const kioskPost = async (url, body) => {
    const headers = () => ({ headers: { "X-Kiosk-Token": sessionStorage.getItem("kiosk_token") || "" } });
    try {
      return await axios.post(url, body, headers());
    } catch (err) {
      if (err.response?.status === 401) {
        const cfg = await axios.get(`${API}/signatures/kiosk/config`).catch(() => null);
        if (cfg && !cfg.data?.pin_required) {
          const tk = await axios.post(`${API}/signatures/kiosk/verify-pin`, { pin: "" });
          sessionStorage.setItem("kiosk_token", tk.data.kiosk_token || "");
          return await axios.post(url, body, headers());
        }
        sessionStorage.removeItem("kiosk_token");
        alert("Sessione kiosk scaduta: ricarica la pagina e inserisci di nuovo il PIN");
      }
      throw err;
    }
  };

  const confirmAllSignatures = async () => {
    const b64 = captureCurrentCanvas();
    const finalSigs = [...signatures];
    finalSigs[2] = b64;
    const primarySig = finalSigs[2] || finalSigs[1] || finalSigs[0];
    try {
      await kioskPost(`${API}/signatures/kiosk/complete`, {
        rental_id: data.rental_id,
        signature: primarySig,
        all_signatures: finalSigs,
      });
      setStatus("done");
      setTimeout(() => {
        setStatus("idle");
        setData(null);
        setStep(1);
        setHasDrawn(false);
        setSignatures([null, null, null]);
      }, 4000);
    } catch (err) {
      if (err.response?.status !== 401) alert("Errore durante il salvataggio della firma");
    }
  };

  const cancelKiosk = async () => {
    try {
      await kioskPost(`${API}/signatures/kiosk/cancel-from-kiosk`, { rental_id: data?.rental_id });
    } catch {}
    setStatus("idle");
    setData(null);
    setStep(1);
    setHasDrawn(false);
    setSignatures([null, null, null]);
  };

  const fmtDate = (d) => {
    if (!d) return "-";
    const [y, m, dd] = d.split("-");
    return `${dd}/${m}/${y}`;
  };

  const rental = data?.rental || {};
  const client = data?.client || {};
  const vehicle = data?.vehicle || {};
  const days = rental.start_date && rental.end_date
    ? Math.max(1, Math.ceil((new Date(rental.end_date) - new Date(rental.start_date)) / 86400000))
    : "-";

  // PIN screen
  if (!authenticated || checkingPin) {
    return (
      <div className="fixed inset-0 bg-white flex flex-col items-center justify-center">
        <div className="text-center space-y-6 w-full max-w-sm px-6">
          <div className="w-20 h-20 rounded-full bg-red-50 flex items-center justify-center mx-auto">
            <Lock className="w-10 h-10 text-red-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">RG Rent Kiosk</h1>
            <p className="text-slate-500 mt-2 text-base">Inserisci il PIN per accedere</p>
          </div>
          {checkingPin ? (
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-red-600" />
          ) : (
            <div className="space-y-5">
              {/* PIN dots display */}
              <div className="flex items-center justify-center gap-3">
                {[...Array(6)].map((_, i) => (
                  <div
                    key={i}
                    className={`w-4 h-4 rounded-full border-2 transition-all ${
                      i < pinInput.length
                        ? "bg-red-600 border-red-600"
                        : "bg-transparent border-slate-300"
                    }`}
                  />
                ))}
              </div>
              {pinError && (
                <div className="flex items-center justify-center gap-2 text-red-600 text-sm">
                  <AlertCircle className="w-4 h-4" /> {pinError}
                </div>
              )}
              <NumPad value={pinInput} onChange={setPinInput} maxLength={6} />
              <Button
                data-testid="kiosk-pin-submit"
                onClick={verifyPin}
                disabled={pinInput.length < 4}
                className="w-full bg-red-600 hover:bg-red-700 text-white rounded-xl h-12 text-base"
              >
                Accedi
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // IDLE
  if (status === "idle" || status === "loading") {
    return (
      <div className="fixed inset-0 bg-white flex flex-col items-center justify-center">
        <div className="text-center space-y-6">
          <div className="w-20 h-20 rounded-full bg-red-50 flex items-center justify-center mx-auto">
            <Monitor className="w-10 h-10 text-red-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">RG Rent</h1>
            <p className="text-slate-500 mt-2 text-lg">In attesa di firma...</p>
          </div>
          <div className="flex items-center justify-center gap-3">
            <div className="w-3 h-3 bg-red-600 rounded-full animate-pulse" />
            <div className="w-3 h-3 bg-red-600 rounded-full animate-pulse" style={{ animationDelay: "0.3s" }} />
            <div className="w-3 h-3 bg-red-600 rounded-full animate-pulse" style={{ animationDelay: "0.6s" }} />
          </div>
        </div>
      </div>
    );
  }

  // DONE
  if (status === "done") {
    return (
      <div className="fixed inset-0 bg-white flex flex-col items-center justify-center">
        <div className="text-center space-y-6">
          <div className="w-20 h-20 rounded-full bg-emerald-50 flex items-center justify-center mx-auto">
            <Check className="w-10 h-10 text-emerald-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-emerald-700">Firma Completata</h1>
            <p className="text-slate-500 mt-2">Grazie, {client.name} {client.surname}</p>
          </div>
        </div>
      </div>
    );
  }

  // Signature canvas
  const renderSignatureCanvas = (label) => (
    <div className="flex flex-col h-full">
      <h2 className="text-xl font-bold text-slate-900 text-center shrink-0">{label}</h2>
      <div className="text-center py-1 shrink-0">
        <p className="text-lg font-bold text-slate-800">{client.name} {client.surname}</p>
        <p className="text-xs text-slate-500">Firma nel riquadro sottostante</p>
      </div>
      <div className="relative border-2 border-slate-300 rounded-xl bg-white overflow-hidden min-h-0" style={{ touchAction: "none", maxHeight: "200px", flex: "1 1 200px" }}>
        <canvas
          ref={canvasRef}
          className="w-full h-full"
          style={{ cursor: "crosshair" }}
          onPointerDown={startDraw}
          onPointerMove={draw}
          onPointerUp={endDraw}
          onPointerLeave={endDraw}
        />
        {!hasDrawn && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <p className="text-slate-300 text-2xl font-medium">Firma qui</p>
          </div>
        )}
      </div>
    </div>
  );

  const totalSteps = 6;

  return (
    <div className="fixed inset-0 bg-white flex flex-col">
      {/* Progress */}
      <div className="h-2 bg-slate-100 w-full">
        <div className="h-full bg-red-600 transition-all duration-500" style={{ width: `${(step / totalSteps) * 100}%` }} />
      </div>

      {/* Header */}
      <div className="px-8 py-4 border-b border-slate-200 flex items-center justify-between bg-slate-50">
        <div className="flex items-center gap-3">
          <PenTool className="w-6 h-6 text-red-600" />
          <div>
            <p className="text-base font-bold text-slate-900">Firma Contratto {rental.contract_number}</p>
            <p className="text-sm text-slate-500">{client.name} {client.surname} - {vehicle.brand} {vehicle.model} ({vehicle.plate})</p>
          </div>
        </div>
        <span className="text-sm text-slate-500 font-medium">Passaggio {step}/{totalSteps}</span>
      </div>

      {/* Content */}
      <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4 max-w-3xl mx-auto w-full">
        {step === 1 && (
          <div className="space-y-5">
            <h2 className="text-2xl font-bold text-slate-900 text-center">Autorizzazione Privacy</h2>
            {privacyPages.length > 0 ? (
              <div className="space-y-4">
                {privacyPages.map((page, i) => (
                  <img key={i} src={`data:image/png;base64,${page}`} alt={`Privacy ${i + 1}`} className="w-full rounded-xl border border-slate-200 shadow-sm" draggable={false} />
                ))}
              </div>
            ) : (
              <div className="py-10 text-center text-slate-500"><Loader2 className="w-8 h-8 animate-spin mx-auto" /></div>
            )}
          </div>
        )}
        {step === 2 && renderSignatureCanvas("Firma Accettazione Privacy")}
        {step === 3 && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-slate-900 text-center">Dati Cliente e Riepilogo</h2>
            <div className="p-5 bg-slate-50 rounded-xl border border-slate-200 space-y-4">
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Dati Cliente</p>
              <div className="grid grid-cols-2 gap-4 text-base">
                <div><span className="text-slate-500">Nome:</span> <span className="font-semibold text-slate-900">{client.name} {client.surname}</span></div>
                <div><span className="text-slate-500">Cod. Fiscale:</span> <span className="font-semibold text-slate-900">{client.fiscal_code || "-"}</span></div>
                <div><span className="text-slate-500">Indirizzo:</span> <span className="font-semibold text-slate-900">{client.address || "-"}{client.city ? `, ${client.city}` : ""}</span></div>
                <div><span className="text-slate-500">Telefono:</span> <span className="font-semibold text-slate-900">{client.phone || "-"}</span></div>
                <div><span className="text-slate-500">Patente:</span> <span className="font-semibold text-slate-900">{client.license_number || "-"}</span></div>
                <div><span className="text-slate-500">Email:</span> <span className="font-semibold text-slate-900">{client.email || "-"}</span></div>
              </div>
            </div>
            <div className="p-5 bg-slate-50 rounded-xl border border-slate-200 space-y-4">
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Riepilogo Noleggio</p>
              <div className="grid grid-cols-2 gap-4 text-base">
                <div><span className="text-slate-500">Veicolo:</span> <span className="font-semibold text-slate-900">{vehicle.brand} {vehicle.model}</span></div>
                <div><span className="text-slate-500">Targa:</span> <span className="font-semibold text-slate-900">{vehicle.plate}</span></div>
                <div><span className="text-slate-500">Consegna:</span> <span className="font-semibold text-slate-900">{fmtDate(rental.start_date)} {rental.start_time || ""}</span></div>
                <div><span className="text-slate-500">Riconsegna:</span> <span className="font-semibold text-slate-900">{fmtDate(rental.end_date)} {rental.end_time || ""}</span></div>
                <div><span className="text-slate-500">Durata:</span> <span className="font-semibold text-slate-900">{days} giorni</span></div>
                <div><span className="text-slate-500">Tariffa:</span> <span className="font-semibold text-slate-900">{rental.daily_rate} EUR/giorno</span></div>
                <div><span className="text-slate-500">Totale:</span> <span className="font-bold text-slate-900 text-xl">{rental.total_amount?.toFixed(2)} EUR</span></div>
                <div><span className="text-slate-500">Acconto:</span> <span className="font-semibold text-slate-900">{(rental.deposit_amount || 0).toFixed(2)} EUR</span></div>
              </div>
            </div>
          </div>
        )}
        {step === 4 && renderSignatureCanvas("Firma Conferma Dati")}
        {step === 5 && (
          <div className="space-y-5">
            <h2 className="text-2xl font-bold text-slate-900 text-center">Termini e Condizioni</h2>
            <div className="p-6 bg-slate-50 rounded-xl border border-slate-200 text-base text-slate-700 leading-relaxed space-y-4">
              <p><b>1. Condizioni generali.</b> Il presente contratto di noleggio e' regolato dalle condizioni qui riportate. Il veicolo viene consegnato in buono stato e dovra' essere restituito nelle medesime condizioni.</p>
              <p><b>2. Conclusione del contratto.</b> Il contratto si perfeziona con la consegna del veicolo e la sottoscrizione del presente modulo.</p>
              <p><b>3. Obblighi del Locatario.</b> Il Locatario si impegna a: utilizzare il veicolo con diligenza, rispettare il Codice della Strada, non cedere a terzi il veicolo, non utilizzarlo per competizioni o gare.</p>
              <p><b>4. Assicurazione.</b> Il veicolo e' coperto da assicurazione RCA. Eventuali danni non coperti dalla polizza sono a carico del Locatario.</p>
              <p><b>5. Restituzione.</b> Il veicolo deve essere restituito nella data e ora concordate. Ritardi comportano l'addebito di giorni aggiuntivi.</p>
              <p><b>6. Carburante.</b> Il veicolo viene consegnato con un determinato livello di carburante e deve essere restituito con lo stesso livello.</p>
              <p><b>7. Danni e furto.</b> In caso di incidente o furto, il Locatario deve avvisare immediatamente l'Azienda e le autorita' competenti.</p>
              <p><b>8. Penali.</b> Il mancato rispetto delle condizioni comporta l'applicazione delle penali previste e la risoluzione del contratto.</p>
              <p className="font-semibold mt-4 text-slate-900">Proseguendo con la firma, il Locatario dichiara di aver letto e accettato integralmente le condizioni sopra riportate.</p>
            </div>
          </div>
        )}
        {step === 6 && renderSignatureCanvas("Firma Contratto")}
      </div>

      {/* Footer */}
      <div className="px-6 py-3 border-t border-slate-200 bg-slate-50 flex justify-between items-center shrink-0">
        {/* Annulla - always visible on left */}
        <Button
          variant="outline"
          onClick={cancelKiosk}
          data-testid="kiosk-cancel-btn"
          className="rounded-xl px-5 h-11 text-sm text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
        >
          <X className="w-4 h-4 mr-2" /> Annulla
        </Button>

        {/* Center - Pulisci (only on signature steps) */}
        <div>
          {(step === 2 || step === 4 || step === 6) && (
            <Button variant="outline" onClick={clearCanvas} className="rounded-xl px-5 h-11 text-sm text-slate-600 border-slate-300">
              <RotateCcw className="w-4 h-4 mr-2" /> Pulisci Firma
            </Button>
          )}
        </div>

        {/* Right side actions */}
        <div>
          {(step === 1 || step === 3) && (
            <Button onClick={() => setStep(step + 1)} className="bg-red-600 hover:bg-red-700 text-white rounded-xl px-8 h-12 text-base">
              Avanti <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
          )}
          {step === 5 && (
            <Button onClick={() => setStep(6)} className="bg-red-600 hover:bg-red-700 text-white rounded-xl px-8 h-12 text-base">
              <PenTool className="w-5 h-5 mr-2" /> Firma
            </Button>
          )}
          {(step === 2 || step === 4) && (
            <Button onClick={saveCurrentSignature} disabled={!hasDrawn} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl px-8 h-12 text-base">
              <Check className="w-5 h-5 mr-2" /> Conferma e Avanti
            </Button>
          )}
          {step === 6 && (
            <Button onClick={confirmAllSignatures} disabled={!hasDrawn} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl px-8 h-12 text-base">
              <Check className="w-5 h-5 mr-2" /> Conferma Firma
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
